import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck, ArrowRight, Loader2, KeyRound } from "lucide-react";
import TenantCrest from "../components/TenantCrest";

// Brand-integrity note (Feb 2026 executive directive):
// The institutional login MUST NOT render any third-party school photograph,
// campus, gate, fence, or externally identifiable educational property.
// The prior Pexels HERO photograph (id 29242200) was removed. The login
// presentation now uses a purpose-designed, institution-neutral Education360
// premium digital environment composed entirely of CSS + inline SVG.
// `tenant.branding.cover_image_url` is deliberately NOT consumed here even
// if a legacy value remains in tenant configuration.

const ROLE_TO_LANDING = {
  executive: "executive", principal: "executive", deputy_head: "executive",
  board: "executive", administrator: "school-admin",
  teacher: "staff", parent: "parent", student: "student",
  finance: "finance", admissions: "admissions", boarding: "boarding",
  health: "medical", transport: "transport", librarian: "library", hr: "hr",
};

export default function SchoolLogin() {
  const { school } = useParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("principal@highlands.edu");
  const [password, setPassword] = useState("Highlands2026!");
  const [tenant, setTenant] = useState(() => {
    try {
      const cached = sessionStorage.getItem(`edu360.tenant.${school}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && Date.now() - parsed.ts < 10 * 60 * 1000) return parsed.data;
      }
    } catch (_) {}
    return null;
  });
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    api.tenantBySlug(school)
      .then(({ data }) => {
        if (!alive) return;
        setTenant(data);
        try { sessionStorage.setItem(`edu360.tenant.${school}`, JSON.stringify({ ts: Date.now(), data })); } catch (_) {}
      })
      .catch(() => alive && setError("This school could not be found on Education360™."));
    return () => { alive = false; };
  }, [school]);

  useEffect(() => {
    if (user) navigate(`/education360/${school}/${ROLE_TO_LANDING[user.role] || "executive"}`, { replace: true });
  }, [user, navigate, school]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const data = await login(email, password);
      toast.success(`Welcome, ${data.user.full_name}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign in failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  if (!tenant) {
    return (
      <div className="min-h-screen grid place-items-center bg-[color:var(--hl-page)] p-8 edu360-root">
        <div className="text-slate-500 text-sm flex items-center gap-2" data-testid="school-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          {error || `Resolving ${school}.education360.thomdi.com…`}
        </div>
      </div>
    );
  }

  // Institution identity comes from the resolved tenant; visual environment
  // is entirely institution-neutral (no photograph, no third-party imagery).
  const primary = tenant?.branding?.primary_color || "#002147";
  const accent = tenant?.branding?.accent_color || "#D9A24A"; // heritage gold (restrained)

  return (
    <div
      className="edu360-root edu360-login-shell min-h-screen relative overflow-hidden"
      data-testid="school-login"
      style={{ "--hl-navy": primary, "--hl-gold": accent }}
    >
      {/* ============================================================
          Premium institution-neutral Education360 environment.
          Composed entirely of CSS + inline SVG. Contains no photograph,
          no third-party institutional imagery, no campus, gate, fence,
          crest, signage or watermark other than the authorised Highlands
          tenant crest rendered by <TenantCrest/> below.
          ============================================================ */}
      <div className="absolute inset-0 edu360-login-bg" aria-hidden>
        {/* Layer 1 — deep institutional navy field (solid + gradient depth) */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(1200px 800px at 18% 12%, #01305A 0%, #002147 42%, #00152F 100%)",
          }}
        />
        {/* Layer 2 — subtle architectural geometry (fine gold-hairline grid) */}
        <svg
          className="absolute inset-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid slice"
          viewBox="0 0 1600 900"
          aria-hidden
          focusable="false"
        >
          <defs>
            <pattern id="edu360-grid" width="80" height="80" patternUnits="userSpaceOnUse">
              <path
                d="M 80 0 L 0 0 0 80"
                fill="none"
                stroke="#D9A24A"
                strokeOpacity="0.055"
                strokeWidth="1"
              />
            </pattern>
            <pattern id="edu360-dots" width="24" height="24" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="0.8" fill="#D9A24A" fillOpacity="0.06" />
            </pattern>
            <radialGradient id="edu360-halo" cx="18%" cy="14%" r="55%">
              <stop offset="0%" stopColor="#0A3466" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#00152F" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width="1600" height="900" fill="url(#edu360-halo)" />
          <rect width="1600" height="900" fill="url(#edu360-grid)" />
          <rect width="1600" height="900" fill="url(#edu360-dots)" />
          {/* Restrained architectural arcs — quiet, authoritative */}
          <g stroke="#D9A24A" strokeOpacity="0.09" fill="none">
            <circle cx="1420" cy="820" r="260" strokeWidth="1" />
            <circle cx="1420" cy="820" r="380" strokeWidth="1" />
            <circle cx="1420" cy="820" r="520" strokeWidth="1" />
          </g>
          {/* Hairline vertical baseline (executive typographic column) */}
          <line
            x1="120"
            y1="60"
            x2="120"
            y2="840"
            stroke="#D9A24A"
            strokeOpacity="0.14"
            strokeWidth="1"
          />
        </svg>
        {/* Layer 3 — restrained bottom-left gold sweep (heritage detail) */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(360deg, rgba(217,162,74,0.06) 0%, rgba(217,162,74,0) 42%)",
          }}
        />
        {/* Layer 4 — soft top-right lighting for premium depth */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(600px 400px at 92% 8%, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0) 60%)",
          }}
        />
      </div>

      <div className="relative z-10 min-h-screen grid lg:grid-cols-[1.15fr_1fr] gap-0">
        {/* Brand pane */}
        <div className="hidden lg:flex flex-col justify-between p-14 xl:p-20 text-white">
          <div className="flex items-center gap-4 animate-fade-in">
            <div className="w-16 h-16 bg-white/10 backdrop-blur border border-white/20 rounded-xl p-1.5 shadow-2xl flex-shrink-0">
              <TenantCrest tenant={tenant} size="xl" className="!w-full !h-full" testId="login-crest-desktop" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.4em] text-white/60">Since {tenant.founded_year || tenant.established_year || "1998"}</div>
              <div className="font-display font-bold text-2xl tracking-tight mt-1">{tenant.name}</div>
              {tenant.branding?.values_line && (
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/60 mt-1" style={{ color: accent }}>{tenant.branding.values_line}</div>
              )}
            </div>
          </div>

          <div className="max-w-xl animate-fade-in-up">
            {/* Platform ↔ tenant hierarchy strap (Education360 = platform) */}
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-white/55 mb-5">
              <span className="font-semibold" style={{ color: accent }}>Education360™</span>
              <span className="w-6 h-px" style={{ background: "rgba(217,162,74,0.35)" }} />
              <span>Secure Institutional Access</span>
            </div>
            <h1 className="font-display font-bold text-5xl xl:text-6xl leading-[1.05] tracking-tight text-white">
              A single window into every child, every classroom, every campus.
            </h1>
            <p className="text-white/75 text-base mt-6 leading-relaxed">
              Admissions, academics, attendance, boarding, finance and executive intelligence — unified in one board-ready platform for international education leadership.
            </p>
          </div>

          <div>
            {tenant?.branding?.motto && (
              <div className="italic font-display text-2xl tracking-tight" style={{ color: accent }}>
                "{tenant.branding.motto}"
              </div>
            )}
            <div className="mt-4 text-[10px] tracking-[0.32em] uppercase text-white/45 not-italic">
              A THOM Digital Infrastructure platform
            </div>
          </div>
        </div>

        {/* Glass login card */}
        <div className="flex items-center justify-center p-6 sm:p-10 lg:p-14">
          <div className="w-full max-w-md animate-fade-in-up">
            {/* Mobile brand row */}
            <div className="lg:hidden flex items-center gap-3 mb-8 text-white">
              <div className="w-12 h-12 bg-white/10 backdrop-blur border border-white/20 rounded-lg p-1 flex-shrink-0">
                <TenantCrest tenant={tenant} size="lg" className="!w-full !h-full" testId="login-crest-mobile" />
              </div>
              <div>
                <div className="font-display font-bold text-lg leading-tight">{tenant.name}</div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-white/70">Education360™</div>
              </div>
            </div>

            <div
              className="relative rounded-2xl border border-white/25 bg-white shadow-2xl p-8 sm:p-10"
              style={{ boxShadow: "0 30px 80px rgba(0,21,47,0.45), 0 0 0 1px rgba(217,162,74,0.18) inset" }}
            >
              <div className="absolute -top-3 left-8 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.32em] text-[color:var(--hl-navy)] shadow-lg" style={{ background: accent }}>
                Institutional Sign-in
              </div>

              <div className="mb-1 text-[10px] uppercase tracking-[0.32em] text-slate-500 font-semibold">Tenant · {tenant.short_name || tenant.name}</div>
              <h2 className="font-display font-bold text-3xl text-[color:var(--hl-navy)] leading-tight tracking-tight">
                Welcome back.
              </h2>
              <p className="text-sm text-slate-500 mt-2">
                Academic Year {tenant.academic_year} · {tenant.current_term}
              </p>

              <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="login-form">
                <div>
                  <label htmlFor="login-email" className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">Email address</label>
                  <input
                    id="login-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email"
                    className="mt-1.5 w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--hl-gold)]/40 focus:border-[color:var(--hl-gold)]/60 transition"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label htmlFor="login-password" className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">Password</label>
                    <button type="button" className="text-[11px] text-[color:var(--hl-navy)] hover:text-[color:var(--hl-gold)] hover:underline flex items-center gap-1" onClick={() => toast.info("A recovery link is sent by your school administrator on request.")} data-testid="link-forgot">
                      <KeyRound className="w-3 h-3" />Forgot?
                    </button>
                  </div>
                  <div className="mt-1.5 relative">
                    <input
                      id="login-password"
                      type={showPw ? "text" : "password"}
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password"
                      className="w-full px-4 py-3 pr-11 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--hl-gold)]/40 focus:border-[color:var(--hl-gold)]/60 transition"
                    />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700" aria-label="Toggle password visibility" data-testid="toggle-password">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="rounded border-slate-300" data-testid="remember-me" />
                  Keep me signed in on this device
                </label>

                {error && (
                  <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 flex items-start gap-2" data-testid="login-error">
                    <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-red-500 flex-shrink-0" />{error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm text-white transition disabled:opacity-60 group"
                  style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary} 60%, ${accent} 140%)` }}
                >
                  {loading ? (<><Loader2 className="w-4 h-4 animate-spin" />Signing in…</>) : (<>Sign in securely<ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" /></>)}
                </button>
              </form>

              <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />Encrypted session</span>
                <button className="text-slate-500 hover:text-[color:var(--hl-navy)] underline" onClick={() => setDemoOpen(!demoOpen)} data-testid="toggle-demo">
                  {demoOpen ? "Hide" : "Show"} demo accounts
                </button>
              </div>

              {demoOpen && (
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs" data-testid="demo-accounts">
                  {[
                    ["Principal", "principal@highlands.edu"],
                    ["Teacher", "teacher@highlands.edu"],
                    ["Parent", "parent@highlands.edu"],
                    ["Student", "student@highlands.edu"],
                    ["Finance", "finance@highlands.edu"],
                    ["Admissions", "admissions@highlands.edu"],
                    ["Boarding", "boarding@highlands.edu"],
                    ["Executive", "executive@highlands.edu"],
                  ].map(([label, e]) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => { setEmail(e); setPassword("Highlands2026!"); }}
                      data-testid={`demo-${label.toLowerCase()}`}
                      className="text-left px-3 py-2 border border-slate-200 rounded-md hover:border-[color:var(--hl-gold)] hover:bg-[color:var(--hl-gold)]/10 transition-colors"
                    >
                      <div className="font-semibold text-slate-800">{label}</div>
                      <div className="text-slate-500 truncate text-[10px] mono">{e}</div>
                    </button>
                  ))}
                  <div className="col-span-2 mt-1 text-[10px] text-slate-400">
                    Password for all demo accounts: <span className="mono text-slate-600">Highlands2026!</span>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-between text-[11px] text-white/70">
              <Link to={`/education360/apply/${school}/enquire`} className="hover:text-white flex items-center gap-1" data-testid="link-enquire">
                New to {tenant.short_name || "our school"}? Enquire <ArrowRight className="w-3 h-3" />
              </Link>
              <span className="mono">© {new Date().getFullYear()} {tenant.name}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
